import { useState, useEffect, lazy, Suspense } from "react";
import owl from './owl.png';

import Icon from './Icon';
import boo from './boo.svg';
import "./styles.css";
import "./link_react-mobile-table/index.css";

// MobileTable is not above the fold — lazy-load it so it doesn't block
// initial paint. Saves a few KB from the main chunk and parallelises with
// the API fetch that populates the showtimes.
const MobileTable = lazy(() => import("@yrobot/react-mobile-table"));

// API URL - change for production
// API URL - auto-detect production vs local
const API_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'http://localhost:5001'
  : 'https://api.durianbit.com/filmhouse';

// Function to get base URL based on environment
const getBaseUrl = () => {
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? window.location.origin
    : 'https://boxofficeowl.kenif.xyz';
};

// Function to generate URL-safe key from title
const generateUrlKey = (title, year) => {
  const urlKey = title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
  
  return `${urlKey}-${year || 'unknown'}`;
};

// Function to copy text to clipboard with feedback
const copyToClipboard = async (text, setIsCopied) => {
  try {
    await navigator.clipboard.writeText(text);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  } catch (err) {
    console.error('Failed to copy text: ', err);
  }
};

// Status Pill Component - simple text pill
const StatusPill = ({ status, isNewRelease }) => {
  const isComingSoon = status === 'coming_soon';
  
  // Priority: Coming Soon > New Release > Now Showing
  let label, bgColor, textColor;
  if (isComingSoon) {
    label = 'Coming Soon';
    bgColor = 'rgba(0,0,0,0.6)';
    textColor = '#fff';
  } else if (isNewRelease) {
    label = 'New Release';
    bgColor = '#dc2626'; // Bright red
    textColor = '#fff';
  } else {
    label = 'Now Showing';
    bgColor = '#e0d3ce'; // Cream
    textColor = '#7f1d1d'; // Dark red
  }
  
  return (
    <span style={{
      display: 'inline-flex', // Flex for vertical centering
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: bgColor,
      color: textColor,
      fontSize: '0.5em',
      padding: '3px 10px',
      borderRadius: '9999px', // Fully rounded pill
      lineHeight: '1.2',
    }}>
      {label}
    </span>
  );
};

// Tab Button Component
const TabButton = ({ active, onClick, children, variant }) => {
  // Different styles for "Now Showing" tab
  const isNowShowing = variant === 'now_showing';
  
  let bgColor, textColor, borderColor;
  if (active) {
    if (isNowShowing) {
      bgColor = '#ffffff';
      textColor = '#dc2626'; // Red
      borderColor = '#ffffff';
    } else {
      bgColor = '#e0d3ce';
      textColor = '#4a0a0a';
      borderColor = '#e0d3ce';
    }
  } else {
    bgColor = 'transparent';
    textColor = '#e0d3ce';
    borderColor = '#e0d3ce';
  }
  
  return (
    <button
      onClick={onClick}
      style={{
        padding: '8px 16px',
        backgroundColor: bgColor,
        color: textColor,
        border: `1px solid ${borderColor}`,
        borderRadius: '20px',
        cursor: 'pointer',
        fontSize: '0.85em',
        fontWeight: active ? 'bold' : 'normal',
        transition: 'all 0.2s ease',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
};

// Skeleton Components
const SkeletonPulse = ({ style, className }) => (
  <div 
    className={`skeleton-pulse ${className || ''}`}
    style={{
      backgroundColor: '#5a1515',
      borderRadius: '8px',
      animation: 'skeleton-shimmer 1.5s ease-in-out infinite',
      ...style
    }}
  />
);

const MovieCardSkeleton = () => (
  <div 
    style={{
      flex: "0 0 auto",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      width: "100px",
    }}
  >
    <SkeletonPulse 
      style={{
        width: "100px",
        height: "150px",
        borderRadius: "8px",
      }}
    />
    <div style={{
      width: "100%",
      marginTop: "0.3em",
      marginBottom: "0.3em",
      paddingTop: "0.3em",
      paddingBottom: "0.3em",
    }}>
      <SkeletonPulse 
        style={{
          width: "80%",
          height: "12px",
          margin: "0 auto",
        }}
      />
    </div>
  </div>
);

const TableRowSkeleton = () => (
  <div style={{
    display: 'flex',
    borderBottom: '1px solid #e0d3ce',
    padding: '10px 0',
  }}>
    <SkeletonPulse style={{ width: '25%', height: '20px', marginRight: '10px' }} />
    <SkeletonPulse style={{ width: '30%', height: '20px', marginRight: '10px' }} />
    <SkeletonPulse style={{ width: '25%', height: '20px', marginRight: '10px' }} />
    <SkeletonPulse style={{ width: '15%', height: '20px' }} />
  </div>
);

const ShowtimesTableSkeleton = () => (
  <div style={{
    padding: '0 16px',
    marginTop: '1em',
  }}>
    <div style={{
      display: 'flex',
      borderBottom: '1px solid #e0d3ce',
      padding: '10px 0',
      marginBottom: '4px',
    }}>
      <SkeletonPulse style={{ width: '25%', height: '20px', marginRight: '10px', backgroundColor: '#555' }} />
      <SkeletonPulse style={{ width: '30%', height: '20px', marginRight: '10px', backgroundColor: '#555' }} />
      <SkeletonPulse style={{ width: '25%', height: '20px', marginRight: '10px', backgroundColor: '#555' }} />
      <SkeletonPulse style={{ width: '15%', height: '20px', backgroundColor: '#555' }} />
    </div>
    {[...Array(6)].map((_, i) => (
      <TableRowSkeleton key={i} />
    ))}
  </div>
);

const LoadingUI = () => (
  <>
    <span className="subtitle">for filmhouse.sg</span>
    <div className="marquee" style={{
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-around",
      overflow: "hidden",
    }}>
      <div style={{
        display: "flex",
        flexWrap: "nowrap",
        overflowX: "auto",
        gap: "0.8em",
        padding: "0 1em",
        margin: "1.5em 0 0.5em 0",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
      }}>
        {[...Array(8)].map((_, i) => (
          <MovieCardSkeleton key={i} />
        ))}
      </div>
    </div>
    <span className="subsubtitle" style={{
      fontSize: "0.5em",
      marginBottom: "1.2em",
    }}>
      <SkeletonPulse style={{ width: '200px', height: '16px', margin: '0 auto', display: 'block' }} />
    </span>
    <ShowtimesTableSkeleton />
  </>
);

export default function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [allFilms, setAllFilms] = useState([]);
  const [displayedFilms, setDisplayedFilms] = useState([]);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'now_showing', 'coming_soon'
  const [dataShowtimes, setDataShowtimes] = useState([]);
  const [filteredDataShowtimes, setFilteredDataShowtimes] = useState([]);
  const [showFilteredDataShowtimes, setShowFilteredDataShowtimes] = useState(false);
  const [selectedTitle, setSelectedTitle] = useState(-1);
  const [rawShowtimes, setRawShowtimes] = useState([]);
  const [isCopied, setIsCopied] = useState(false);
  const [sortBy, setSortBy] = useState('date'); // 'date' or 'title'
  const [previousSortBy, setPreviousSortBy] = useState(null); // Remember sort before film selection

  const newDataHeaders = [["Title", "Showtime", "Book"]];

  // Format a showtime for display: "Fri, 7th August, 5:00 pm".
  // The day of week is abbreviated to three letters and commas separate
  // day / date / time so the column stays narrow and the Title column can
  // take the freed width. Used by BOTH the table builder and the click
  // handler's match lookup, so the two strings can never drift apart.
  const formatShowtime = (dateStr, timeStr, is4k) => {
    const suffix = is4k ? ' [4K]' : '';
    const raw = (dateStr || '').trim();
    // Expect "Friday 7th August". Abbreviate the weekday, then comma-separate.
    const m = raw.match(/^(\w+)\s+(.*)$/);
    if (m) {
      const dow = m[1].slice(0, 3);
      const rest = m[2].trim();
      return `${dow}, ${rest}, ${timeStr || ''}${suffix}`.trim();
    }
    // Unrecognised shape: fall back to the original spacing rather than mangle it.
    return `${raw} ${timeStr || ''}${suffix}`.trim();
  };

  // Sort table data
  // Parse date string like "Friday 6th February 4:30 pm" into a sortable Date
  const parseDateString = (dateStr) => {
    try {
      // Extract day number, month, and time
      const match = dateStr.match(/(\d+)(?:st|nd|rd|th)\s+(\w+)\s+(\d+):(\d+)\s*(am|pm)/i);
      if (!match) return new Date(0);
      
      const [, day, month, hour, minute, ampm] = match;
      const months = {
        'january': 0, 'february': 1, 'march': 2, 'april': 3, 'may': 4, 'june': 5,
        'july': 6, 'august': 7, 'september': 8, 'october': 9, 'november': 10, 'december': 11
      };
      
      let hours = parseInt(hour);
      if (ampm.toLowerCase() === 'pm' && hours !== 12) hours += 12;
      if (ampm.toLowerCase() === 'am' && hours === 12) hours = 0;
      
      // Assume current year or next year if month has passed
      const now = new Date();
      let year = now.getFullYear();
      const monthNum = months[month.toLowerCase()];
      if (monthNum < now.getMonth()) year++;
      
      return new Date(year, monthNum, parseInt(day), hours, parseInt(minute));
    } catch {
      return new Date(0);
    }
  };

  const sortTableData = (data, sortKey) => {
    if (data.length <= 1) return data; // Only header or empty
    const header = data[0];
    const rows = data.slice(1);
    
    const sorted = [...rows].sort((a, b) => {
      if (sortKey === 'title') {
        return a[0].localeCompare(b[0]);
      } else {
        // Sort by date (parse the showtime string)
        const dateA = parseDateString(a[1]);
        const dateB = parseDateString(b[1]);
        return dateA - dateB;
      }
    });
    
    return [header, ...sorted];
  };
  
  // Get sorted data based on current sort
  const getSortedData = () => {
    const data = showFilteredDataShowtimes ? filteredDataShowtimes : dataShowtimes;
    return sortTableData(data, sortBy);
  };

  const resetSelectedTitle = () => {
    setSelectedTitle(-1);
    setShowFilteredDataShowtimes(false);
    setFilteredDataShowtimes([]);
    // Restore previous sort if it was different
    if (previousSortBy !== null) {
      setSortBy(previousSortBy);
      setPreviousSortBy(null);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Sort films for "All" view: New Releases → Now Showing → Coming Soon
  const sortFilmsForAllView = (films) => {
    return [...films].sort((a, b) => {
      // Priority: 1 = New Release, 2 = Now Showing, 3 = Coming Soon
      const getPriority = (film) => {
        if (film.is_new_release && film.status === 'now_showing') return 1;
        if (film.status === 'now_showing') return 2;
        return 3; // coming_soon
      };
      return getPriority(a) - getPriority(b);
    });
  };

  // Filter films by tab
  useEffect(() => {
    if (activeTab === 'all') {
      setDisplayedFilms(sortFilmsForAllView(allFilms));
    } else if (activeTab === 'now_showing') {
      setDisplayedFilms(allFilms.filter(f => f.status === 'now_showing'));
    } else if (activeTab === 'coming_soon') {
      setDisplayedFilms(allFilms.filter(f => f.status === 'coming_soon'));
    }
    // Reset selection when changing tabs
    if (selectedTitle !== -1) {
      resetSelectedTitle();
    }
  }, [activeTab, allFilms]);

  // Handle movie selection
  const handleMovieSelection = (movie, index) => {
    if (selectedTitle === index) {
      resetSelectedTitle();
      return;
    }

    setSelectedTitle(index);
    setPreviousSortBy(sortBy); // Remember current sort before switching
    setSortBy('date'); // Reset to date sort when selecting a film
    
    // Build filtered showtimes for this movie
    let newData = [...newDataHeaders];
    dataShowtimes.forEach((item) => {
      if (item[0] === movie.title) {
        newData.push(item);
      }
    });
    
    setFilteredDataShowtimes(newData);
    setShowFilteredDataShowtimes(true);

    // Scroll to table
    const scrollToTable = (retryCount = 0) => {
      const showtimesTable = document.getElementById('showtimes-table');
      if (showtimesTable) {
        const rect = showtimesTable.getBoundingClientRect();
        const isTableVisible = (
          rect.top >= 0 &&
          rect.left >= 0 &&
          rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
          rect.right <= (window.innerWidth || document.documentElement.clientWidth)
        );

        if (!isTableVisible) {
          const yOffset = -20;
          const y = rect.top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      } else if (retryCount < 10) {
        setTimeout(() => scrollToTable(retryCount + 1), 100);
      }
    };

    setTimeout(() => scrollToTable(), 300);
  };

  // Handle URL parameters on load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const movieParam = params.get('movie');
    
    if (movieParam && displayedFilms.length > 0) {
      const movieIndex = displayedFilms.findIndex(movie => movie.urlKey === movieParam);
      if (movieIndex !== -1) {
        handleMovieSelection(displayedFilms[movieIndex], movieIndex);
        
        setTimeout(() => {
          const movieCard = document.querySelector(`[data-movie-key="${movieParam}"]`);
          if (movieCard) {
            movieCard.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
          }
        }, 300);
      }
    }
  }, [displayedFilms]);

  // Fetch data from Filmhouse API (or skip if the edge function already
  // inlined it into window.__BOO_FILMS__). The inlined payload is a trimmed
  // films array (not the full { films: [...] } envelope) because the edge
  // function normalizes before writing.
  const fetchData = async () => {
    try {
      let filmsArray;
      if (typeof window !== 'undefined' && Array.isArray(window.__BOO_FILMS__)) {
        filmsArray = window.__BOO_FILMS__;
      } else {
        const filmsRes = await fetch(`${API_URL}/api/films`);
        const filmsData = await filmsRes.json();
        filmsArray = filmsData.films || [];
      }

      // Process films - dedupe and merge (now_showing takes precedence)
      const filmMap = new Map();

      filmsArray.forEach(film => {
        const key = film.id || film.title;
        const existing = filmMap.get(key);
        
        // If film already exists and current is now_showing, or film doesn't exist
        if (!existing || film.status === 'now_showing') {
          filmMap.set(key, {
            ...film,
            urlKey: generateUrlKey(film.title, film.year),
            // Keep showtimes from now_showing version
            showtimes: film.status === 'now_showing' ? film.showtimes : (existing?.showtimes || film.showtimes || [])
          });
        }
      });
      
      const processedFilms = Array.from(filmMap.values());
      setAllFilms(processedFilms);
      
      // Build showtimes table data
      const tableData = [["Title", "Showtime", "Book"]];
      const rawST = [];
      
      processedFilms.forEach(film => {
        (film.showtimes || []).forEach(st => {
          // Parse date and time
          const dateStr = st.date || '';
          const timeStr = st.time || '';
          const is4k = st.is_4k ? ' [4K]' : '';
          
          tableData.push([
            film.title,
            formatShowtime(dateStr, timeStr, st.is_4k),
            '>>'
          ]);
          
          rawST.push({
            title: film.title,
            book_url: st.book_url,
            date: st.date,
            time: st.time,
            is_4k: st.is_4k
          });
        });
      });
      
      setDataShowtimes(tableData);
      setRawShowtimes(rawST);
      setIsLoading(false);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Get counts for tabs
  const nowShowingCount = allFilms.filter(f => f.status === 'now_showing').length;
  const comingSoonCount = allFilms.filter(f => f.status === 'coming_soon').length;

  return (
    <div className="App" style={{
      backgroundColor: "#4a0a0a",
      minHeight: '100vh',
    }}>
      <div>
        <div style={{
          color: "#e0d3ce",
          padding: "8px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
        }}>
          
          <span className="title" style={{
            height: 'fit-content',
            display: 'inline',
            flexDirection: 'column',
          }}>
            <img src={boo} alt="box office owl icon" style={{
              display: 'inline',
              height: '6vw',
              width: 'auto',
              marginRight: '2vw',
              cursor: 'pointer',
              transition: 'opacity 0.2s ease',
            }} 
            onClick={() => resetSelectedTitle()}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = "0.8";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = "1";
            }}
            />
            <span style={{
              cursor: 'pointer',
            }}
            onClick={() => resetSelectedTitle()}>Box Office Owl</span>
          </span>
          
          {isLoading ? (
            <LoadingUI />
          ) : (
            <>
              <span className="subtitle">for filmhouse.sg</span>
              
              {/* Tab Toggles */}
              <div style={{
                display: 'flex',
                gap: '10px',
                justifyContent: 'center',
                margin: '1em 0',
                flexWrap: 'wrap',
              }}>
                <TabButton 
                  active={activeTab === 'all'} 
                  onClick={() => setActiveTab('all')}
                >
                  All Films ({allFilms.length})
                </TabButton>
                <TabButton 
                  active={activeTab === 'now_showing'} 
                  onClick={() => setActiveTab('now_showing')}
                  variant="now_showing"
                >
                  Now Showing ({nowShowingCount})
                </TabButton>
                <TabButton 
                  active={activeTab === 'coming_soon'} 
                  onClick={() => setActiveTab('coming_soon')}
                >
                  Coming Soon ({comingSoonCount})
                </TabButton>
              </div>

              {/* Films Carousel */}
              <div className="marquee" style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-around",
                overflow: "hidden",
              }}>
                <div className="marquee__inner" aria-hidden="true" style={{
                  margin: "0.5em 0 0.5em 0",
                  overflow: "scroll",
                }}>
                  {displayedFilms.map((movie, index) => (
                    <span 
                      key={movie.urlKey} 
                      data-movie-key={movie.urlKey}
                      className="marquee__inner__item" 
                      style={{
                        margin: "0.3em",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        padding: "0.3em",
                        backgroundColor: "transparent",
                        borderRadius: "12px",
                        position: 'relative',
                      }} 
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-4px)";
                        e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.3)";
                        e.currentTarget.style.backgroundColor = "#4a0a0a";
                        e.currentTarget.style.margin = "0";
                        e.currentTarget.style.padding = "0.6em";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "none";
                        e.currentTarget.style.backgroundColor = "transparent";
                        e.currentTarget.style.margin = "0.3em";
                        e.currentTarget.style.padding = "0.3em";
                      }}
                      onClick={() => {
                        if (selectedTitle === index) {
                          resetSelectedTitle();
                        } else {
                          handleMovieSelection(movie, index);
                        }
                      }}
                    >
                      <img
                        src={movie.poster || `https://via.placeholder.com/100x150?text=${encodeURIComponent(movie.title)}`}
                        style={{
                          width: "100px",
                          height: "150px",
                          objectFit: "cover",
                          borderRadius: "8px",
                          transition: "all 0.2s ease",
                        }}
                        loading={index < 4 ? "eager" : "lazy"}
                        fetchpriority={index < 2 ? "high" : "auto"}
                        decoding="async"
                        width="100"
                        height="150"
                        alt={`Poster for ${movie.title}`}
                        onError={(e) => {
                          e.target.src = `https://via.placeholder.com/100x150/4a0a0a/e0d3ce?text=${encodeURIComponent(movie.title.substring(0, 10))}`;
                        }}
                      />
                      <div style={{
                        width: "100%",
                        backgroundColor: index === selectedTitle ? "#666" : null,
                        borderRadius: "0.3em",
                        marginTop: "0.3em",
                        marginBottom: "0.3em",
                        paddingTop: "0.3em",
                        paddingBottom: "0.3em",
                        lineHeightStep: "0.5em",
                        lineHeight: "1.4",
                        transition: "all 0.2s ease",
                      }}>
                        {activeTab === 'all' && (
                          <StatusPill status={movie.status} isNewRelease={movie.is_new_release} />
                        )}
                        <span className="marquee__inner__item__title" style={{
                          fontSize: "0.5em",
                          inset: "0.3em",
                          display: 'block',
                          marginTop: activeTab === 'all' ? '4px' : '0',
                        }}>
                          {movie.title} {movie.year && `(${movie.year})`}
                        </span>
                      </div>
                    </span>
                  ))}
                </div>
              </div>
            </>
          )}
          
          {selectedTitle === -1 && !isLoading && (
            <span className="subsubtitle" style={{
              fontSize: "0.5em",
              marginBottom: "1.2em",
            }}>
              Tap on a title for more information
            </span>
          )}
        </div>

        {/* Selected Film Details */}
        {selectedTitle > -1 && displayedFilms[selectedTitle] && (
          <div className="selectedTitle" style={{
            backgroundColor: "#666",
            marginLeft: "1em",
            marginTop: "0",
            marginRight: "1em",
            marginBottom: "1.5em",
            padding: "1em",
            color: "#fff",
            borderRadius: "1em",
          }}>
            <div className="selectedTitle__title" style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              textAlign: "left",
            }}>
              <span className="selectedTitle__title__text" style={{
                fontWeight: "bold",
              }}>
                {displayedFilms[selectedTitle].title} 
                {displayedFilms[selectedTitle].year && ` (${displayedFilms[selectedTitle].year})`}
              </span>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "1em",
              }}>
                <button
                  onClick={() => {
                    const shareUrl = `${getBaseUrl()}?movie=${displayedFilms[selectedTitle].urlKey}`;
                    copyToClipboard(shareUrl, setIsCopied);
                  }}
                  style={{
                    background: "none",
                    border: "1px solid transparent",
                    borderRadius: "4px",
                    color: isCopied ? "#4ade80" : "#fff",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.3em",
                    padding: "0.4em 0.6em",
                    fontSize: "0.85em",
                    transition: "all 0.2s ease",
                    opacity: isCopied ? "1" : "0.85",
                  }}
                  onMouseEnter={(e) => {
                    if (!isCopied) {
                      e.currentTarget.style.opacity = "1";
                      e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.1)";
                      e.currentTarget.style.border = "1px solid rgba(255, 255, 255, 0.2)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isCopied) {
                      e.currentTarget.style.opacity = "0.85";
                      e.currentTarget.style.backgroundColor = "transparent";
                      e.currentTarget.style.border = "1px solid transparent";
                    }
                  }}
                >
                  <Icon
                    name={isCopied ? "checkmark" : "link-outline"}
                    style={{
                      fontSize: '1.4em',
                      marginTop: '-2px',
                      color: isCopied ? "#4ade80" : "inherit"
                    }}
                  />
                  {isCopied ? "Copied!" : "Copy Link"}
                </button>
                <button
                  className="selectedTitle__title__close"
                  onClick={() => resetSelectedTitle()}
                  style={{
                    background: "none",
                    border: "1px solid transparent",
                    borderRadius: "4px",
                    color: "#fff",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    padding: "0.4em",
                    transition: "all 0.2s ease",
                    opacity: "0.85",
                    marginLeft: "0.5em",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = "1";
                    e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.1)";
                    e.currentTarget.style.border = "1px solid rgba(255, 255, 255, 0.2)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = "0.85";
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.border = "1px solid transparent";
                  }}
                >
                  <Icon
                    name="close"
                    style={{
                      fontSize: '1.5em'
                    }}
                  />
                </button>
              </div>
            </div>

            {/* Film metadata */}
            <div style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "0.5em",
              marginTop: "0.5em",
              fontSize: "0.85em",
            }}>
              {displayedFilms[selectedTitle].vote_average > 0 && (
                <span style={{
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  padding: '4px 12px',
                  borderRadius: '9999px',
                  display: 'inline-flex',
                  alignItems: 'center',
                }}>
                  ⭐ {displayedFilms[selectedTitle].vote_average.toFixed(1)}/10
                </span>
              )}
              {displayedFilms[selectedTitle].is_new_release && (
                <span style={{
                  backgroundColor: '#ec4899',
                  padding: '4px 12px',
                  borderRadius: '9999px',
                  fontWeight: 'bold',
                  display: 'inline-flex',
                  alignItems: 'center',
                }}>
                  🆕 New Release
                </span>
              )}
              {displayedFilms[selectedTitle].rating && (
                <span style={{
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  padding: '4px 12px',
                  borderRadius: '9999px',
                  display: 'inline-flex',
                  alignItems: 'center',
                }}>
                  {displayedFilms[selectedTitle].rating}
                </span>
              )}
              {displayedFilms[selectedTitle].duration && (
                <span style={{
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  padding: '4px 12px',
                  borderRadius: '9999px',
                  display: 'inline-flex',
                  alignItems: 'center',
                }}>
                  {displayedFilms[selectedTitle].duration}
                </span>
              )}
              {displayedFilms[selectedTitle].genre && (
                <span style={{
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  padding: '4px 12px',
                  borderRadius: '9999px',
                  display: 'inline-flex',
                  alignItems: 'center',
                }}>
                  {displayedFilms[selectedTitle].genre}
                </span>
              )}
            </div>

            {displayedFilms[selectedTitle].director && (
              <div style={{
                marginTop: "0.6em",
                fontSize: "0.85em",
                opacity: 0.9,
                textAlign: 'left',
              }}>
                🎬 Directed by {displayedFilms[selectedTitle].director}
              </div>
            )}

            {(displayedFilms[selectedTitle].overview || displayedFilms[selectedTitle].description) && (
              <div className="selectedTitle__overview" style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "start",
                alignItems: "left",
                marginTop: "0.8em",
                textAlign: "left",
                fontSize: "0.8em",
              }}>
                <span className="selectedTitle__overview__text">
                  {displayedFilms[selectedTitle].overview || displayedFilms[selectedTitle].description}
                </span>
              </div>
            )}

            {/* No showtimes notice */}
            {(!displayedFilms[selectedTitle].showtimes || displayedFilms[selectedTitle].showtimes.length === 0) && (
              <div style={{
                marginTop: '1em',
                padding: '0.5em 1em',
                backgroundColor: 'rgba(255,255,255,0.1)',
                borderRadius: '8px',
                textAlign: 'center',
                fontSize: '0.85em',
                opacity: 0.8,
              }}>
                {displayedFilms[selectedTitle].status === 'coming_soon' ? (
                  <>Showtimes not yet available</>
                ) : (
                  <>No showtimes currently available</>
                )}
              </div>
            )}
          </div>
        )}

        {/* Showtimes Table */}
        <div style={{
          overflow: 'scroll',
        }}>
          {/* No showtimes at all notice */}
          {!isLoading && selectedTitle === -1 && dataShowtimes.length <= 1 && (
            <div style={{
              padding: '2em',
              textAlign: 'center',
              color: '#e0d3ce',
              opacity: 0.8,
            }}>
              🎟️ No showtimes currently available
            </div>
          )}
          
          {/* Showtimes table - show when there's data */}
          {!isLoading && ((selectedTitle === -1 && dataShowtimes.length > 1) || (selectedTitle !== -1 && displayedFilms[selectedTitle]?.showtimes?.length > 0)) && (
            <div id="showtimes-table">
              {/* Sort controls - only show when no film selected */}
              {selectedTitle === -1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '8px',
                  padding: '8px 16px',
                  color: '#e0d3ce',
                  fontSize: '0.8em',
                }}>
                  <span style={{ opacity: 0.7 }}>Sort by:</span>
                  <button
                    onClick={() => setSortBy('date')}
                    style={{
                      background: sortBy === 'date' ? '#e0d3ce' : 'transparent',
                      color: sortBy === 'date' ? '#4a0a0a' : '#e0d3ce',
                      border: '1px solid #e0d3ce',
                      borderRadius: '12px',
                      padding: '2px 10px',
                      cursor: 'pointer',
                      fontSize: '0.9em',
                    }}
                  >
                    Date
                  </button>
                  <button
                    onClick={() => setSortBy('title')}
                    style={{
                      background: sortBy === 'title' ? '#e0d3ce' : 'transparent',
                      color: sortBy === 'title' ? '#4a0a0a' : '#e0d3ce',
                      border: '1px solid #e0d3ce',
                      borderRadius: '12px',
                      padding: '2px 10px',
                      cursor: 'pointer',
                      fontSize: '0.9em',
                    }}
                  >
                    Title
                  </button>
                </div>
              )}
              <Suspense fallback={<TableRowSkeleton />}>
                <MobileTable
                  data={getSortedData()}
                  columnMinWidth={82}
                  onClick={(param) => {
                    const { column, line } = param;
                    if (column === 2 && line !== 0) {
                      // Find the matching showtime
                      const tableData = getSortedData();
                      const rowTitle = tableData[line][0];
                      const rowShowtime = tableData[line][1];

                      // Find matching raw showtime
                      const match = rawShowtimes.find(st => {
                        const stShowtime = formatShowtime(st.date, st.time, st.is_4k);
                        return st.title === rowTitle && stShowtime === rowShowtime;
                      });

                      if (match && match.book_url) {
                        window.open(match.book_url, "_blank");
                      }
                    }
                  }}
                />
              </Suspense>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
